/* eslint-disable react/display-name, @typescript-eslint/no-empty-function */
/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-12 15:53:13
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Modal, Progress, message } from 'antd'
import FileSaver from 'file-saver'
import styles from './index.module.scss'
import iconImg from '../../../images/remove_watermark_modal_icon.png'
import vipCrown from '../../../images/vip_icon_1.png'
import {
  useAppDispatch,
  useAppSelector,
  useContactUrl,
  usePayment,
  useSensorsRole,
  useVip
} from '../../../app/hooks'
import classNames from 'classnames'
import {
  queryTaskStatus,
  removeWatermarkDownload
} from '../../../actions/photo'
import sensors from '../../../utils/sensors'
import desktopLib from '../../../utils/desktopLib'
import SinglePreview from './SinglePreview'
import MultiPreview from './MultiPreview'
import actionTypes from '../../../constants/actionTypes'
import { ExclamationCircleOutlined } from '@ant-design/icons'
// eslint-disable-next-line react/display-name

export default ({
  visible,
  taskID,
  photoNum,
  onCancel,
  onRetry,
  fileName,
  isBatch = false,
  dataSource = ''
}) => {
  const [progress, setProgress] = useState(0)
  const [successNum, setSuccessNum] = useState(0)
  const [imgs, setImgs] = useState([])
  const [isFailed, setIsFailed] = useState(false)
  const groupID = useAppSelector((state) => state.group.groupId)
  const role = useSensorsRole()
  const contactUsUrl = useContactUrl('去水印')

  const vipInfo = useVip()
  const payment = usePayment()
  const dispatch = useAppDispatch()

  const handleReportSaEvent = (clickItem: string) => {
    const params: {
      clickItem: string
      groupID: string
      role: string
      popType: string
      photoNum: any
      fromPlace?: string
    } = {
      clickItem,
      groupID,
      role,
      popType: isBatch ? 'batchRemoveWatermark' : 'singleRemoveWatermark',
      photoNum
    }
    if (dataSource) {
      params.fromPlace =
        dataSource === 'legacy' ? 'watermarkContent' : 'preciseSearch'
    }
    sensors.track('web_click_pop_remove_watermark', params)
  }

  const queryStatus = useCallback(() => {
    dispatch(queryTaskStatus({ taskID })).then((res) => {
      if (res.taskID !== taskID) {
        return
      }

      if (res.status === -121) {
        setProgress((prev) =>
          Math.min(90, prev + Math.floor(Math.random() * (isBatch ? 5 : 90)))
        )
        setTimeout(queryStatus, 300)
      } else if (res.status === -124) {
        setIsFailed(true)
      } else {
        setProgress(100)
        setImgs(res.outUrls)
        setSuccessNum(res.totalSuccessNum)
      }
    })
  }, [dispatch, taskID])

  const handleDownload = () => {
    handleReportSaEvent('download')

    if (!vipInfo.isVip) {
      payment.open(isBatch ? 'batchRemoveWatermark' : 'singleRemoveWatermark')
    } else {
      dispatch(removeWatermarkDownload({ taskID })).then((res) => {
        if (res.status === -402) {
          /**
           * vip 会员去水印次数不够
           */

          sensors.track('web_show_toast_remove_watermark_times_limit', {
            groupID,
            role,
            fromPlace: 'removeWatermark'
          })

          message.warning({
            content: '去水印次数不足，请升级到团队超级会员',
            duration: 1,
            onClose: () => {
              payment.openSvip(
                isBatch ? 'batchRemoveWatermark' : 'singleRemoveWatermark'
              )
            }
          })
        } else if (res.status === -403) {
          /**
           * svip 会员去水印次数不够
           */

          sensors.track('web_get_action_pop_remove_watermark_times_depletion', {
            actionType: 'showPop',
            groupID,
            role,
            fromPlace: 'removeWatermark'
          })

          Modal.confirm({
            title: '下载失败',
            content: '团队的“编辑水印/去水印”次数已用完',
            okText: '联系客服',
            cancelText: '我知道了',
            icon: <ExclamationCircleOutlined />,
            onOk: () => {
              sensors.track(
                'web_get_action_pop_remove_watermark_times_depletion',
                {
                  actionType: 'contactUs',
                  groupID,
                  role,
                  fromPlace: 'removeWatermark'
                }
              )

              if (desktopLib.isDesktop) {
                desktopLib.openContact(contactUsUrl)
              } else {
                window.open(
                  contactUsUrl,
                  '_blank',
                  'width=500,height=600,right=0,bottom=0'
                )
              }
            },
            onCancel: () => {
              sensors.track(
                'web_get_action_pop_remove_watermark_times_depletion',
                {
                  actionType: 'iKnow',
                  groupID,
                  role,
                  fromPlace: 'removeWatermark'
                }
              )
            }
          })
        } else {
          if (isBatch) {
            let a: HTMLAnchorElement | null = document.createElement('a')
            a.href = res.url + '?response-content-disposition=attachment;'
            a.download = fileName
            a.click()
            a = null
          } else {
            FileSaver.saveAs(res.url, fileName)
          }

          dispatch({
            type: actionTypes.CLEAR_CHECKED_PHOTO
          })
          dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
          dispatch({ type: actionTypes.SET_CHECK_ALL, data: false })

          dispatch({ type: actionTypes.FOLDER_CLEAR_CHECKED_PHOTO })
          dispatch({ type: actionTypes.FOLDER_CLEAR_UNCHECKED_PHOTO })
          dispatch({ type: actionTypes.FOLDER_SET_CHECK_ALL, data: false })

          handleClose()
        }
      })
    }
  }

  const resetStatus = () => {
    setProgress(0)
    setImgs([])
    setIsFailed(false)
  }

  const handleClose = () => {
    onCancel()
    resetStatus()
  }

  useEffect(() => {
    if (visible) {
      queryStatus()

      if (!vipInfo.isVip) {
        sensors.track('web_show_page_openapp_workgroupvip', {
          place: 'photoPreview',
          functionType: isBatch
            ? 'batchRemoveWatermark'
            : 'singleRemoveWatermark'
        })
      }
    }
  }, [visible, queryStatus, vipInfo.isVip])

  let title = ''

  if (isFailed) {
    title = isBatch ? '批量去水印失败' : '去水印失败，请重试'
  } else if (progress === 100) {
    title = isBatch
      ? `🎉 批量去水印已完成，共${successNum}张`
      : '🎉 去水印已完成'
  } else {
    title = `正在去水印，已完成${progress}%`
  }

  return (
    <Modal
      open={visible}
      wrapClassName={classNames(styles.modal, {
        [styles.hidden]: payment.paymentStatus.visible
      })}
      maskStyle={{
        visibility: payment.paymentStatus.visible ? 'hidden' : 'visible'
      }}
      footer={null}
      width={progress === 100 ? 780 : 600}
      mask={{ closable: false }}
      onCancel={() => {
        handleReportSaEvent('close')
        handleClose()
      }}
      destroyOnHidden={true}
    >
      <div className={styles.container}>
        <div className={styles.title}>{title}</div>
        {progress === 100 ? (
          isBatch ? (
            <MultiPreview imgs={imgs} successNum={successNum} />
          ) : (
            <SinglePreview imgUrl={imgs[0]} />
          )
        ) : (
          <div
            className={classNames(styles.body, {
              [styles.failed]: isFailed
            })}
          >
            <img src={iconImg} className={styles.icon} alt="" />

            <Progress
              percent={isFailed ? 100 : progress}
              showInfo={false}
              style={{ width: 240 }}
              strokeColor={isFailed ? '#F54A45' : ''}
            />
          </div>
        )}

        <div className={styles.footer}>
          <Button
            style={{
              marginRight: 12,
              width: 120
            }}
            onClick={() => {
              handleReportSaEvent(isFailed ? 'iKnow' : 'cancel')
              handleClose()
            }}
          >
            {isFailed ? '我知道了' : '取消'}
          </Button>
          {isFailed ? (
            <Button
              type="primary"
              onClick={() => {
                handleReportSaEvent('retry')
                resetStatus()
                onRetry()
              }}
            >
              重试
            </Button>
          ) : (
            <Button
              disabled={progress !== 100}
              type="primary"
              onClick={handleDownload}
            >
              下载到本地
              <img src={vipCrown} className={styles.vipIcon} alt="" />
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
